import React, { useState, useEffect } from 'react';

import { API } from '../config/api';
import { apiFetch } from '../config/http';

// Modal: generar proyecciones desde los hitos de pago de la oferta (#3).
// Un mes por hito; el monto sale del hito (linea.monto * porcentaje/100).
//
// Prop `obligatorio`: cuando es true (encadenado tras aprobar una oferta) se
// oculta el boton Cancelar y no se puede cerrar por backdrop; la unica salida es
// Generar. Asi se garantiza que toda oferta aprobada quede con su proyeccion.
export const ModalGenerarProyecciones = ({ ofertaId, proyectoId, moneda, facturarCon, onCerrar, onGenerado, obligatorio = false }) => {
  const [cargando,  setCargando]  = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error,     setError]     = useState('');
  const [items,     setItems]     = useState([]);

  const simbolo  = moneda === 'Colones' ? '₡' : '$';
  const fmtMonto = (n) => Number(n || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    let activo = true;
    (async () => {
      try {
        const data = await apiFetch(`${API}/ofertas-cliente?id=${ofertaId}`).then(r => r.json());
        const lineas = Array.isArray(data.lineas) ? data.lineas : [];
        const arr = lineas.map(l => {
          const montoLinea = parseFloat(l.monto) || 0;
          const hitos = (l.hitos && l.hitos.length > 0)
            ? l.hitos.map(h => {
                const pct = parseFloat(h.porcentaje) || 0;
                // v42: capturar el id del hito de oferta (OfertaClienteHitos.id) para
                // enlazar la proyeccion generada via ProyeccionFacturacion.hito_id (FK).
                return { nombre: h.nombre_hito || 'Pago', porcentaje: pct, monto: +(montoLinea * pct / 100).toFixed(2), incluir: true, mes: '', oferta_hito_id: h.id };
              })
            : [{ nombre: 'Pago unico', porcentaje: 100, monto: montoLinea, incluir: true, mes: '', oferta_hito_id: null }];
          return {
            linea_oferta_id: l.id,
            item_numero:     l.numero_linea,
            item_desc:       l.descripcion_corta || l.descripcion || '(sin descripcion)',
            monto_contrato:  montoLinea,
            hitos
          };
        });
        if (activo) setItems(arr);
      } catch (e) {
        if (activo) setError('Error cargando la oferta: ' + e.message);
      } finally {
        if (activo) setCargando(false);
      }
    })();
    return () => { activo = false; };
  }, [ofertaId]);

  const setHito = (li, hi, campo, val) => setItems(prev => prev.map((it, i) => i !== li ? it : {
    ...it, hitos: it.hitos.map((h, j) => j !== hi ? h : { ...h, [campo]: val })
  }));

  const totalListos = items.reduce((s, it) => s + it.hitos.filter(h => h.incluir && h.mes).length, 0);

  const generar = async () => {
    setError('');
    const marcados = items.flatMap(it => it.hitos.filter(h => h.incluir).map(h => ({ it, h })));
    if (marcados.length === 0) { setError('Marca al menos un hito.'); return; }
    const faltanMes = marcados.filter(x => !x.h.mes);
    if (faltanMes.length > 0) { setError('Asigna un mes a cada hito marcado (faltan ' + faltanMes.length + ').'); return; }
    setGuardando(true);
    try {
      for (const { it, h } of marcados) {
        const resp = await apiFetch(`${API}/proyeccion-facturacion`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            proyecto_id:     proyectoId,
            linea_oferta_id: it.linea_oferta_id,
            item_numero:     it.item_numero,
            hito_id:         h.oferta_hito_id ?? null,   // v42: enlace a OfertaClienteHitos.id
            descripcion:     it.item_desc + ' - ' + h.nombre,
            facturar_con:    facturarCon || 'JyA Conusa',
            moneda:          moneda || 'Dólares',
            monto:           h.monto,
            fecha_proyectada: h.mes + '-01'
          })
        });
        if (!resp.ok) { const d = await resp.json().catch(() => ({})); throw new Error(d.error || 'Error al guardar una proyeccion'); }
      }
      onGenerado();
    } catch (e) {
      setError(e.message);
      setGuardando(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20 }} onClick={obligatorio ? undefined : onCerrar}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: 660, maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#012E40', marginBottom: 4 }}>Generar proyecciones</div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
          {obligatorio
            ? 'Asigna el mes a cada hito de pago para completar la aprobacion. Podes moverlos luego en la matriz.'
            : 'Asigna el mes a cada hito de pago. El monto sale de la oferta.'}
        </div>
        {error && <div style={{ background: '#FCEBEB', border: '1px solid #f5c6c6', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#A32D2D', marginBottom: 12 }}>{error}</div>}
        {cargando ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 12 }}>Cargando oferta...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#aaa', fontSize: 12 }}>La oferta no tiene lineas.</div>
        ) : (
          items.map((it, li) => (
            <div key={it.linea_oferta_id} style={{ marginBottom: 14, border: '1px solid #eee', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#012E40', marginBottom: 8 }}>
                {it.item_numero}. {it.item_desc} <span style={{ color: '#888', fontWeight: 400 }}>(contrato {simbolo}{fmtMonto(it.monto_contrato)})</span>
              </div>
              {it.hitos.map((h, hi) => (
                <div key={hi} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 120px 130px', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <input type="checkbox" checked={h.incluir} onChange={e => setHito(li, hi, 'incluir', e.target.checked)} />
                  <span style={{ fontSize: 12, color: h.incluir ? '#333' : '#bbb' }}>{h.nombre} <span style={{ color: '#999' }}>{h.porcentaje}%</span></span>
                  <span style={{ fontSize: 12, textAlign: 'right', color: h.incluir ? '#012E40' : '#bbb', fontWeight: 500 }}>{simbolo}{fmtMonto(h.monto)}</span>
                  <input type="month" value={h.mes} disabled={!h.incluir}
                    onChange={e => setHito(li, hi, 'mes', e.target.value)}
                    style={{ padding: '5px 6px', border: '1px solid #ddd', borderRadius: 5, fontSize: 11, opacity: h.incluir ? 1 : 0.4 }} />
                </div>
              ))}
            </div>
          ))
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          {!obligatorio && (
            <button onClick={onCerrar} style={{ padding: '8px 18px', border: '1px solid #e0e0e0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
          )}
          <button onClick={generar} disabled={guardando || cargando || totalListos === 0}
            style={{ padding: '8px 18px', background: '#87B749', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: (guardando || cargando || totalListos === 0) ? 'default' : 'pointer', opacity: (guardando || cargando || totalListos === 0) ? 0.6 : 1 }}>
            {guardando ? 'Generando...' : `Generar ${totalListos} proyeccion${totalListos === 1 ? '' : 'es'}`}
          </button>
        </div>
      </div>
    </div>
  );
};
